// src/components/Achievements/Achievements.jsx
// Componentes presentacionales compartidos del sistema de logros.
import React from 'react';
import { FaLock, FaMedal } from 'react-icons/fa';
import {
  SPRITES, shade, EMBLEM_COLOR, avatarColor, buildTrophies, rarityOf,
} from '../../lib/achievements';
import { PixelArt, creatureAvatar } from '../../lib/pixelart';
import './Achievements.css';

export function PixelIcon({ sprite, color, size = 46 }) {
  const grid = SPRITES[sprite] || SPRITES.check;
  const cols = grid[0].length, rows = grid.length;
  const pal = { o: shade(color, -75), '#': color, '*': shade(color, 75) };
  const rects = [];
  grid.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) {
      const f = pal[row[x]];
      if (f) rects.push(<rect key={`${x}-${y}`} x={x} y={y} width="1.02" height="1.02" fill={f} />);
    }
  });
  return (
    <svg className="ach-pix" width={size} height={size} viewBox={`0 0 ${cols} ${rows}`} shapeRendering="crispEdges" aria-hidden="true">
      {rects}
    </svg>
  );
}

export function Avatar({ u = {}, emblem, frame, size = 96 }) {
  const em  = emblem || u.avatarEmblem || 'photo';
  const fr  = frame  || u.avatarFrame  || 'basic';
  const pad = Math.max(3, Math.round(size * 0.05));
  const seed = u.username || u.name || '?';
  let inner, bg;
  if (em !== 'photo' && SPRITES[em]) {
    inner = <PixelIcon sprite={em} color={EMBLEM_COLOR[em] || '#22d3ee'} size={Math.round(size * 0.72)} />;
    bg = `radial-gradient(circle at 50% 38%, ${EMBLEM_COLOR[em]}26, #0b1219)`;
  } else if (u.avatar) {
    inner = <img className="ach-avatar-img" src={u.avatar} alt="" />;
    bg = avatarColor(seed);
  } else {
    // Avatar por defecto: bicho pixel determinístico (en vez de iniciales)
    const c = creatureAvatar(seed);
    inner = <PixelArt sprite={c.sprite} fit={Math.round(size * 0.84)} className="ach-avatar-px" />;
    bg = c.bg;
  }
  return (
    <div className={`ach-avatar ach-frame-${fr}`} style={{ width: size, height: size, padding: pad }}>
      <div className="ach-avatar-inner" style={{ background: bg }}>{inner}</div>
    </div>
  );
}

// Vitrina de logros — reutilizada en Recompensas y en el perfil público.
// Soporta logros secretos: si secret && !unlocked → se muestra como "???".
export function TrophyShowcase({ u, title, lockedAreSecret = false }) {
  const cats = buildTrophies(u);
  const all = cats.flatMap((c) => c.items);
  const unlocked = all.filter((t) => t.unlocked).length;
  return (
    <div className="ach-showcase">
      {title && (
        <div className="ach-showcase-head">
          <span className="ach-showcase-title"><FaMedal style={{ marginRight: 8 }} /> {title}</span>
          <span className="ach-showcase-count">{unlocked}/{all.length}</span>
        </div>
      )}
      <div className="ach-progress">
        <div className="ach-progress-bar"><div className="ach-progress-fill" style={{ width: `${(unlocked / all.length) * 100}%` }} /></div>
        <span className="ach-progress-label">{Math.round((unlocked / all.length) * 100)}% completado</span>
      </div>

      {cats.map((c) => {
        const got = c.items.filter((i) => i.unlocked).length;
        return (
          <div key={c.cat} className="ach-cat">
            <div className="ach-cat-head">
              <span className="ach-cat-title">{c.cat}</span>
              <span className="ach-cat-count">{got}/{c.items.length}</span>
            </div>
            <div className="ach-grid">
              {c.items.map((t) => {
                const hidden = (t.secret || lockedAreSecret) && !t.unlocked;
                const rar = rarityOf(t);
                return (
                  <div key={t.id} className={`ach-card ${t.unlocked ? 'unlocked' : 'locked'} ${hidden ? 'secret' : ''}`}>
                    <div className="ach-card-icon">
                      <PixelIcon sprite={hidden ? 'mystery' : t.pix} color={t.unlocked ? t.color : '#566678'} />
                    </div>
                    {!t.unlocked && <div className="ach-card-lock"><FaLock size={15} /></div>}
                    <div className="ach-card-label">{hidden ? '???' : t.label}</div>
                    <div className="ach-card-req">{hidden ? 'Logro secreto' : `${t.unlocked ? '✓ ' : ''}${t.req}`}</div>
                    {!hidden && (
                      <div className="ach-card-rarity" style={{ color: rar.color }}>
                        <span className="ach-rarity-dot" style={{ background: rar.color }} /> {rar.rarity}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
